import React from 'react';

export interface Location {
  path: string;
  id: string;
  name?: string;
}

export interface Map {
  viewBox: string;
  locations: Location[];
  label?: string;
}

export interface BaseMapProps {
  map: Map;
  className?: string;
  locationClassName?: string | ((location: Location, index: number) => string);
  onLocationMouseOver?: (event: React.MouseEvent<SVGPathElement, MouseEvent>) => void;
  onLocationMouseOut?: (event: React.MouseEvent<SVGPathElement, MouseEvent>) => void;
  onLocationMouseMove?: (event: React.MouseEvent<SVGPathElement, MouseEvent>) => void;
  onLocationFocus?: (event: React.FocusEvent<SVGPathElement>) => void;
  onLocationBlur?: (event: React.FocusEvent<SVGPathElement>) => void;
  childrenBefore?: React.ReactElement;
  childrenAfter?: React.ReactElement;
}

export interface SVGMapProps extends BaseMapProps {
  role?: string;
  locationTabIndex?: number | ((location: Location, index: number) => number);
  locationRole?: string;
  onLocationClick?: (location: Location) => void;
  onLocationKeyDown?: (event: React.KeyboardEvent<SVGPathElement>) => void;
  getIsLocationSelected?: (location: Location, index: number) => boolean;
}

export const SVGMap = (props: SVGMapProps): React.ReactElement => {
  function renderLocationPath(location: Location, index: number) {
    return (
      <g key={location.id}>
        <title>{location.name}</title>
        <path
          id={location.id}
          name={location.name}
          d={location.path}
          className={
            typeof props.locationClassName === 'function'
              ? props.locationClassName(location, index)
              : props.locationClassName
          }
          tabIndex={
            typeof props.locationTabIndex === 'function'
              ? props.locationTabIndex(location, index)
              : props.locationTabIndex
          }
          role={props.locationRole}
          aria-label={location.name}
          aria-checked={props.getIsLocationSelected && props.getIsLocationSelected(location, index)}
          onMouseOver={props.onLocationMouseOver}
          onMouseOut={props.onLocationMouseOut}
          onMouseMove={props.onLocationMouseMove}
          onClick={event =>
            props.onLocationClick &&
            props.onLocationClick(
              props.map.locations.find(
                location => location.id === ((event.target as unknown) as { id: string }).id,
              ) as Location,
            )
          }
          onKeyDown={props.onLocationKeyDown}
          onFocus={props.onLocationFocus}
          onBlur={props.onLocationBlur}
        />
      </g>
    );
  }
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={props.map.viewBox}
      className={props.className}
      role={props.role}
      aria-label={props.map.label}
    >
      {props.childrenBefore}
      {props.map.locations
        .filter(
          (location, index) =>
            !props.getIsLocationSelected || !props.getIsLocationSelected(location, index),
        )
        .map(renderLocationPath)}
      {props.map.locations
        .filter(
          (location, index) =>
            props.getIsLocationSelected && props.getIsLocationSelected(location, index),
        )
        .map(renderLocationPath)}
      {props.childrenAfter}
    </svg>
  );
};
